import * as R from "ramda";
import React from "react";
import * as Autosuggest from "react-autosuggest";
import { FaGraduationCap, FaPlus, FaTrash } from "react-icons/fa";
import styled from "styled-components";

import ActionConfirmationButton from "../../../components/pages/CourseAuthor/ActionConfirmation";
import AutoComplete from "../../../components/pages/CourseAuthor/Autocomplete";
import { Link } from "../../../components/pages/utils";
import { contains, localeCompare } from "../../../utils";
import { AddButton, ButtonRow, CustomSearchInput, FieldSet } from "../../Common/Components/FormComponents";
import { autoCompleteCustomStyles, Section } from "../../Common/Styles/containers";
import { IClass, IClassGoalResponse, ICourse } from "../../types/common";

const GoalLink = styled(Link)`
  align-items: center;
`;

const GoalName = styled.span`
  margin-left: 1rem;
`;

const Legend = styled.legend`
  color: ${({ theme }) => theme.colors.buttonDarkBlue};
`;

const ClassGoal = styled.div`
  margin: 1rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CustomGraduationCap = styled(FaGraduationCap)`
  min-width: 1em;
`;

interface IGoalsProps {
  addGoal: (courseId: string) => void;
  deleteGoal: (coursePosId: number) => void;
  class: IClass;
  allCourses: ICourse[];
}

interface IGoalCourse {
  goal: IClassGoalResponse;
  course: ICourse;
}

const byDisplayName = R.comparator<IGoalCourse>((a, b) => localeCompare(a.course.displayName, b.course.displayName));

const Goals: React.FC<IGoalsProps> = props => {
  const autosuggestRef = React.useRef<AutoComplete<ICourse>>(null);
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedCourse, setSelectedCourse] = React.useState<ICourse | null>(null);

  const chooseGoal = "select-goal";
  const { allCourses, addGoal, deleteGoal } = props;
  const notDeletedGoals = props.class.goals.elems.filter(({ deleted }) => !deleted);
  const addNewGoal = () => {
    const courseIds = notDeletedGoals.map(({ course }) => course);
    if (!selectedCourse || courseIds.includes(selectedCourse.courseId)) {
      return;
    }
    addGoal(selectedCourse.courseId);
    setSelectedCourse(null);
    autosuggestRef.current && autosuggestRef.current.clearInput();
  };
  const deleteOldGoal = (coursePosId: number) => () => deleteGoal(coursePosId);
  const onFetchSuggestion = ({ value }: Autosuggest.SuggestionsFetchRequestedParams) => setSearchValue(value);
  const suggestGoals = allCourses.filter(({ displayName }) => contains(searchValue, displayName));
  const classGoals = notDeletedGoals
    .map(goal => ({
      course: allCourses.find(({ courseId }) => courseId === goal.course),
      goal,
    }))
    .filter(({ course }) => !!course)
    .sort(byDisplayName) as IGoalCourse[];

  return (
    <Section>
      <h2>Goals</h2>
      <FieldSet>
        <Legend>Enter a Course</Legend>
        <AutoComplete
          ref={autosuggestRef}
          fetchSuggestions={onFetchSuggestion}
          customStyles={autoCompleteCustomStyles}
          htmlName={chooseGoal}
          items={suggestGoals}
          itemValue={R.propOr("", "displayName")}
          placeholderText="Type here to look for a Course"
          setSelected={setSelectedCourse}
          renderInputComponent={CustomSearchInput}
        />
      </FieldSet>
      <ButtonRow margin={1} width={14}>
        <AddButton disabled={selectedCourse === null} onClick={addNewGoal}>
          <FaPlus />
          Add Goal
        </AddButton>
      </ButtonRow>
      <div>
        {classGoals.map(
          ({ course, goal }) =>
            course && (
              <ClassGoal key={course.courseId}>
                <GoalLink to={`/course-author/${course.courseId}`}>
                  <CustomGraduationCap />
                  <GoalName>{course.displayName}</GoalName>
                </GoalLink>
                <ActionConfirmationButton action="Delete" actionFn={deleteOldGoal(goal.id)} ActionIcon={FaTrash} />
              </ClassGoal>
            )
        )}
      </div>
    </Section>
  );
};

export default Goals;
